import React, {useEffect, useRef, useState} from 'react';
import '../ConfirmWindow/ConfirmationWindow.scss';
import './ExportGuide.scss';
import {FileText, Info, Laptop, X} from 'lucide-react';
import Button from "../../ui/Button/Button";
import {NewChatData} from "../../../store";

export type ExportDevice = 'iPhone' | 'Android';

// Same steps in the first-time popup and in the tips panel next to the upload
export const whatsappExportSteps: Record<ExportDevice, string[]> = {
    'iPhone': [
        'Open WhatsApp and go to Settings → Chats',
        'Tap Export Chat and choose the chat',
        'Choose "Without Media"',
        'Tap Save to Files',
        'Come back here and upload the .zip from Files',
    ],
    'Android': [
        'Open WhatsApp and go to ⋮ → Settings → Chats → Chat history',
        'Tap Export chat and choose the chat',
        'Choose "Without media"',
        'Save the file to your phone (Files or Google Drive)',
        'Come back here and upload the .txt or .zip file',
    ],
};

export const telegramExportSteps = [
    'Open Telegram on your computer (Telegram Desktop or Telegram for macOS) and choose the chat',
    'Click the three dots (⋮ or …) at the top',
    'Click Export chat history',
    'Untick photos, videos and other media (in Telegram Desktop you can also pick JSON or HTML)',
    'Click Export and wait until it says the export is done',
    'Upload result.json or messages.html from the export folder. If there is also a messages2.html, compress the folder and upload the .zip',
];

// Guess the phone from the browser, so the right steps show first
export const detectExportDevice = (): ExportDevice =>
    /android/i.test(navigator.userAgent) ? 'Android' : 'iPhone';

const seenKey = (chatType: NewChatData['chat_type']) => `export_guide_seen_v1_${chatType}`;

// localStorage can be unavailable (private mode, blocked storage); then the guide just shows again next time
export const isExportGuideSeen = (chatType: NewChatData['chat_type']) => {
    try {
        return localStorage.getItem(seenKey(chatType)) === 'true';
    } catch {
        return false;
    }
};

const markExportGuideSeen = (chatType: NewChatData['chat_type']) => {
    try {
        localStorage.setItem(seenKey(chatType), 'true');
    } catch {
        // ignore
    }
};

interface ExportGuideProps {
    chatType: NewChatData['chat_type'];
    onClose: () => void;
}

// Step-by-step export instructions, shown the first time someone imports a chat from each messenger
const ExportGuide = ({chatType, onClose}: ExportGuideProps) => {
    const [device, setDevice] = useState<ExportDevice>(detectExportDevice);
    const dialogRef = useRef<HTMLDivElement>(null);
    const isWhatsapp = chatType === 'Whatsapp';
    const steps = isWhatsapp ? whatsappExportSteps[device] : telegramExportSteps;

    const close = () => {
        markExportGuideSeen(chatType);
        onClose();
    };

    useEffect(() => {
        dialogRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="ConfirmWindow" role="dialog" aria-modal="true" aria-labelledby="exportGuideTitle">
            <div className="ConfirmWindow__blur" onClick={close}></div>

            <div className="ConfirmWindow__form ExportGuide" ref={dialogRef} tabIndex={-1}>
                <button type="button" className="icon-button ExportGuide__close" aria-label="Close" onClick={close}>
                    <X size={18}/>
                </button>

                <div className="row">
                    <div className="icon-tile icon-tile--gradient"><FileText size={20}/></div>
                    <div className="ExportGuide__heading">
                        <h2 id="exportGuideTitle">How to export your {isWhatsapp ? 'WhatsApp' : 'Telegram'} chat</h2>
                        <span>Takes about a minute</span>
                    </div>
                </div>

                {
                    isWhatsapp ?
                        <div className="ExportGuide__tabs" role="group" aria-label="Your phone">
                            {(['iPhone', 'Android'] as ExportDevice[]).map((el) => (
                                <button type="button" key={el} aria-pressed={device === el}
                                        className={device === el ? 'active' : ''} onClick={() => setDevice(el)}>
                                    {el}
                                </button>
                            ))}
                        </div>
                        : <p className="ExportGuide__note">
                            <Laptop size={16}/>
                            <span>Telegram's phone apps can't export chats, so you'll need Telegram on a computer:
                                Telegram Desktop or Telegram for macOS.</span>
                        </p>
                }

                <ol className="ExportGuide__steps">
                    {steps.map((step, i) => (
                        <li key={step}><span className="ExportGuide__stepNumber">{i + 1}</span>{step}</li>
                    ))}
                </ol>

                {
                    isWhatsapp &&
                    <p className="ExportGuide__note">
                        <Info size={16}/>
                        <span><b>Can't find the chat?</b> WhatsApp only lists chats from roughly the last 2 years.
                            Send any message in that chat so it moves to the top, then export it again.</span>
                    </p>
                }

                <div className="row buttonsRow">
                    <Button type={'button'} onClick={close} variant={'primary'} title={'Got it'}/>
                </div>
            </div>
        </div>
    );
};

export default ExportGuide;
